/**
 * Supplier logo resolution.
 *
 * Many suppliers have a valid website but no stored logo URL (or a logo URL that
 * 404s). Rather than fall straight to initials, we walk a chain of public
 * logo/favicon providers derived from the company's domain, trying each until one
 * loads. Ordered best-quality first down to a guaranteed-success fallback.
 */

/**
 * Extract the company domain from a logo URL. Handles favicon-service wrappers
 * (unavatar.io/<domain>, Google s2 favicons ?domain=, Clearbit) and plain website
 * URLs, while skipping image-CDN hosts that aren't the company's own domain.
 */
export function extractDomain(url: string): string | null {
    try {
        // Google Favicon API URLs: pull the target domain out of the query string.
        const googleFaviconMatch = url.match(/[?&](?:domain|url)=(?:https?:\/\/)?([^&]+)/)
        if (googleFaviconMatch?.[1]) return googleFaviconMatch[1].replace(/^www\./, '')
        // unavatar.io / clearbit wrappers: https://unavatar.io/{domain}, logo.clearbit.com/{domain}
        const unavatarMatch = url.match(/unavatar\.io\/([^/?#]+)/)
        if (unavatarMatch?.[1]) return unavatarMatch[1]
        const clearbitMatch = url.match(/logo\.clearbit\.com\/([^/?#]+)/)
        if (clearbitMatch?.[1]) return clearbitMatch[1]
        const ddgMatch = url.match(/icons\.duckduckgo\.com\/ip3\/([^/?#]+)\.ico/)
        if (ddgMatch?.[1]) return ddgMatch[1]
        // Plain URL — take the hostname.
        const parsed = new URL(url)
        const host = parsed.hostname.replace(/^www\./, '')
        // Skip image hosting/favicon CDN domains — we want the company domain, not the CDN.
        const imageHosts = ['imgur.com', 'cloudinary.com', 'amazonaws.com', 'googleusercontent.com', 'gstatic.com', 'google.com']
        if (imageHosts.some(h => host.endsWith(h))) return null
        return host
    } catch {
        return null
    }
}

/** Normalize a free-form website/domain string into a bare hostname. */
export function domainFromWebsite(website?: string | null): string | null {
    if (!website) return null
    const trimmed = website.trim()
    if (!trimmed) return null
    try {
        const parsed = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`)
        return parsed.hostname.replace(/^www\./, '') || null
    } catch {
        return null
    }
}

/**
 * Logo/favicon providers for a domain, tried in order until one loads:
 *   1. Google s2 — high-res favicon. Unknown domain → HTTP 404 with a 16x16 globe
 *                  PNG body, which browsers render as a successful load; SupplierLogo
 *                  rejects it by size (see onLoad) and advances.
 *   2. unavatar  — aggregates many sources; `?fallback=false` makes it a true 404
 *                  with an empty body, so `error` fires and we fall to initials.
 *
 * Removed (verified 2026-10-15):
 *   - logo.clearbit.com: service is gone, connection refused for every domain.
 *   - icons.duckduckgo.com: unknown domain → 404 with a 48x48 placeholder PNG that
 *     browsers render; same size as real icons, so it cannot be told apart and it
 *     would mask the initials fallback.
 */
export function providersFor(domain: string): string[] {
    return [
        `https://www.google.com/s2/favicons?sz=128&domain=${domain}`,
        `https://unavatar.io/${domain}?fallback=false`,
    ]
}

const FAVICON_SERVICE_MARKERS = [
    'unavatar.io',
    'gstatic.com',
    'faviconV2',
    'logo.clearbit.com',
    'icons.duckduckgo.com',
    'google.com/s2/favicons',
]

/**
 * Build the ordered list of logo URLs to attempt for a supplier. A direct logo
 * image (if any) is tried first, then the multi-provider chain derived from the
 * logo URL's domain or — crucially — the supplier's website when no logo exists.
 * Returns an empty array only when there's neither a usable URL nor a website.
 */
export function buildLogoFallbacks(url?: string | null, website?: string | null): string[] {
    const sources: string[] = []
    const isFaviconService = !!url && FAVICON_SERVICE_MARKERS.some(m => url.includes(m))

    // Direct/override URL (an actual logo image) — try it first.
    if (url && !isFaviconService) sources.push(url)

    // Walk the provider chain. Prefer the supplier's website domain — it's the most
    // reliable company-domain signal and rescues suppliers that have a real website
    // but no stored logo URL. Fall back to the domain embedded in the logo URL
    // (e.g. an unavatar/clearbit wrapper) when no website is given. A raw logo's
    // host is often a CDN, so the website wins to avoid building the chain from a CDN.
    const domain = domainFromWebsite(website) || (url ? extractDomain(url) : null)
    if (domain) sources.push(...providersFor(domain))

    // De-dupe while preserving order.
    return [...new Set(sources)]
}
